import React from 'react';
import { Button } from 'react-bootstrap';
import { MarketList, MarketStats } from './MarketList';
import { marketLabels } from './surveyMetrics';

type Props = {
  // The parent's children (and markets added on Setup, not saved yet), or
  // [survey] on a child page.
  markets: any[];
  selectedId: string;
  onSelect: (id: string) => void;
  // Per child survey id; shown as a muted line under each market.
  stats: MarketStats;
  isSales: boolean;
  // False on a child page: Setup in child-open mode cannot add markets.
  canAddMarket?: boolean;
  // Adding a market happens in Setup (Countries field); this switches to that tab.
  onAddMarket: () => void;
  // "+ Add another <market>" per row (parent and child pages, never for sales).
  onAddAnother?: (market: any) => void;
};

// The Markets tab's list column. The detail page places it beside the shared
// Setup/Markets form (see SurveyDetailPage.renderDraftLayout).
export const MarketListCard: React.FC<Props> = ({
  markets, selectedId, onSelect, stats, isSales, canAddMarket = true, onAddMarket, onAddAnother,
}) => {
  const labels = marketLabels(markets);
  const quota = markets.reduce((sum, m) => sum + (Number(m.userLimitCommitted) || 0), 0);
  // Saved markets are the count everywhere (tab badge, header); unsaved ones
  // are named apart, e.g. "Markets (2 + 1 unsaved)".
  const pending = markets.filter((m) => m.draft).length;

  return (
    <div className="dx-card dx-market-list">
      <div className="d-flex justify-content-between align-items-baseline mb-2">
        <h6 className="mb-0">Markets ({markets.length - pending}{pending > 0 ? ` + ${pending} unsaved` : ''})</h6>
        <span className="text-muted small">Quota {quota}</span>
      </div>
      <MarketList markets={markets} selectedId={selectedId} onSelect={onSelect} stats={stats}
        renderRowAction={!isSales && onAddAnother ? (m) => (
          // Not for a market added on Setup: it has no saved row to duplicate yet.
          m.draft ? null : (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 dx-add-another"
              onClick={(e) => { e.stopPropagation(); onAddAnother(m); }}
            >
              + Add another {(labels[m.id] || m.shortName || '').split(' ')[0]}
            </button>
          )
        ) : undefined}
      />
      {!isSales && canAddMarket && (
        <Button variant="outline-primary" size="sm" block className="mt-2" onClick={onAddMarket}>
          + Add market
        </Button>
      )}
    </div>
  );
};
